import { createReducer, on } from '@ngrx/store';
import { Provider, ProviderWebsite, ProviderContact, ProviderLogin } from '../../model/provider.model';
import * as ProviderActions from '../actions/provider.action';

export const providerFeatureKey = 'provider';

export interface ProviderState {
  providers: Provider[];
  loading: boolean;
  error: any;
  websitesLoading: boolean;
  contactsLoading: boolean;
  loginsLoading: boolean;
  websitesError: any;
  contactsError: any;
  loginsError: any;
}

export const initialState: ProviderState = {
  providers: [],
  loading: false,
  error: null,
  websitesLoading: false,
  contactsLoading: false,
  loginsLoading: false,
  websitesError: null,
  contactsError: null,
  loginsError: null,
};

export const providerReducer = createReducer(
  initialState,
  on(ProviderActions.loadProviders, state => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ProviderActions.loadProvidersSuccess, (state, { providers }) => ({
    ...state,
    providers,
    loading: false,
    error: null,
  })),
  on(ProviderActions.loadProvidersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(
    ProviderActions.loadProviderWebsites,
    ProviderActions.createProviderWebsite,
    ProviderActions.updateProviderWebsite,
    ProviderActions.deleteProviderWebsite,
    state => ({
      ...state,
      websitesLoading: true,
      websitesError: null,
    }),
  ),
  on(ProviderActions.loadProviderWebsitesSuccess, (state, { providerId, websites }) => ({
    ...state,
    providers: state.providers.map(provider => (provider.id === providerId ? { ...provider, providerWebsites: websites } : provider)),
    websitesLoading: false,
    websitesError: null,
  })),
  on(ProviderActions.createProviderWebsiteSuccess, (state, { providerId, website }) => ({
    ...state,
    providers: state.providers.map(provider =>
      provider.id === providerId ? { ...provider, providerWebsites: [...(provider.providerWebsites || []), website] } : provider,
    ),
    websitesLoading: false,
    websitesError: null,
  })),
  on(ProviderActions.updateProviderWebsiteSuccess, (state, { providerId, website }) => ({
    ...state,
    providers: state.providers.map(provider =>
      provider.id === providerId
        ? {
            ...provider,
            providerWebsites: (provider.providerWebsites || []).map(w => (w.id === website.id ? website : w)),
          }
        : provider,
    ),
    websitesLoading: false,
    websitesError: null,
  })),
  on(ProviderActions.deleteProviderWebsiteSuccess, (state, { providerId, websiteId }) => ({
    ...state,
    providers: state.providers.map(provider =>
      provider.id === providerId
        ? {
            ...provider,
            providerWebsites: (provider.providerWebsites || []).filter(w => w.id !== websiteId),
          }
        : provider,
    ),
    websitesLoading: false,
    websitesError: null,
  })),
  on(
    ProviderActions.loadProviderWebsitesFailure,
    ProviderActions.createProviderWebsiteFailure,
    ProviderActions.updateProviderWebsiteFailure,
    ProviderActions.deleteProviderWebsiteFailure,
    (state, { error }) => ({
      ...state,
      websitesLoading: false,
      websitesError: error,
    }),
  ),

  on(
    ProviderActions.loadProviderContacts,
    ProviderActions.createProviderContact,
    ProviderActions.updateProviderContact,
    ProviderActions.deleteProviderContact,
    state => ({
      ...state,
      contactsLoading: true,
      contactsError: null,
    }),
  ),
  on(ProviderActions.loadProviderContactsSuccess, (state, { providerId, contacts }) => ({
    ...state,
    providers: state.providers.map(provider => (provider.id === providerId ? { ...provider, providerContacts: contacts } : provider)),
    contactsLoading: false,
    contactsError: null,
  })),
  on(ProviderActions.createProviderContactSuccess, (state, { providerId, contact }) => ({
    ...state,
    providers: state.providers.map(provider =>
      provider.id === providerId ? { ...provider, providerContacts: [...(provider.providerContacts || []), contact] } : provider,
    ),
    contactsLoading: false,
    contactsError: null,
  })),
  on(ProviderActions.updateProviderContactSuccess, (state, { providerId, contact }) => ({
    ...state,
    providers: state.providers.map(provider =>
      provider.id === providerId
        ? {
            ...provider,
            providerContacts: (provider.providerContacts || []).map(c => (c.id === contact.id ? contact : c)),
          }
        : provider,
    ),
    contactsLoading: false,
    contactsError: null,
  })),
  on(ProviderActions.deleteProviderContactSuccess, (state, { providerId, contactId }) => ({
    ...state,
    providers: state.providers.map(provider =>
      provider.id === providerId
        ? {
            ...provider,
            providerContacts: (provider.providerContacts || []).filter(c => c.id !== contactId),
          }
        : provider,
    ),
    contactsLoading: false,
    contactsError: null,
  })),
  on(
    ProviderActions.loadProviderContactsFailure,
    ProviderActions.createProviderContactFailure,
    ProviderActions.updateProviderContactFailure,
    ProviderActions.deleteProviderContactFailure,
    (state, { error }) => ({
      ...state,
      contactsLoading: false,
      contactsError: error,
    }),
  ),

  on(
    ProviderActions.loadProviderLogins,
    ProviderActions.createProviderLogin,
    ProviderActions.updateProviderLogin,
    ProviderActions.updateProviderLoginActiveFlag,
    ProviderActions.deleteProviderLogin,
    state => ({
      ...state,
      loginsLoading: true,
      loginsError: null,
    }),
  ),
  on(ProviderActions.loadProviderLoginsSuccess, (state, { providerId, logins }) => ({
    ...state,
    providers: state.providers.map(provider => (provider.id === providerId ? { ...provider, providerLogins: logins } : provider)),
    loginsLoading: false,
    loginsError: null,
  })),
  on(ProviderActions.createProviderLoginSuccess, (state, { providerId, login }) => ({
    ...state,
    providers: state.providers.map(provider =>
      provider.id === providerId ? { ...provider, providerLogins: [...(provider.providerLogins || []), login] } : provider,
    ),
    loginsLoading: false,
    loginsError: null,
  })),
  on(ProviderActions.updateProviderLoginSuccess, ProviderActions.updateProviderLoginActiveFlagSuccess, (state, { providerId, login }) => ({
    ...state,
    providers: state.providers.map(provider =>
      provider.id === providerId
        ? {
            ...provider,
            providerLogins: (provider.providerLogins || []).map(l => (l.id === login.id ? login : l)),
          }
        : provider,
    ),
    loginsLoading: false,
    loginsError: null,
  })),
  on(ProviderActions.deleteProviderLoginSuccess, (state, { providerId, loginId }) => ({
    ...state,
    providers: state.providers.map(provider =>
      provider.id === providerId
        ? {
            ...provider,
            providerLogins: (provider.providerLogins || []).filter(l => l.id !== loginId),
          }
        : provider,
    ),
    loginsLoading: false,
    loginsError: null,
  })),
  on(
    ProviderActions.loadProviderLoginsFailure,
    ProviderActions.createProviderLoginFailure,
    ProviderActions.updateProviderLoginFailure,
    ProviderActions.updateProviderLoginActiveFlagFailure,
    ProviderActions.deleteProviderLoginFailure,
    (state, { error }) => ({
      ...state,
      loginsLoading: false,
      loginsError: error,
    }),
  ),
);
